import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone, ShieldCheck, Clock, MapPin, Star, Wrench, Flame, Zap, Droplets,
  RefreshCw, AlarmClock, ArrowRight, BadgeCheck, DollarSign, Timer, Award, Search, Waves,
} from "lucide-react";
import { PHONE, PHONE_HREF, IMAGES, REVIEWS, NEIGHBORHOODS, NEARBY_CITIES } from "@/data/site";
import { WH_SERVICES, PLUMBING_SERVICES } from "@/data/services";
import { QuoteForm } from "@/components/QuoteForm";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { BrandsMarquee } from "@/components/BrandsMarquee";

const serviceIcons = {
  "water-heater-installation": Wrench,
  "water-heater-repair": Flame,
  "tank-replacement": RefreshCw,
  "tankless-conversion": Waves,
  "gas-water-heaters": Flame,
  "electric-water-heaters": Zap,
  "emergency-water-heater-service": AlarmClock,
  "drain-cleaning": Droplets,
  "sewer-line-repair": Wrench,
  "leak-detection": Search,
};

const Stars = () => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const manifesto = [
  { n: "01", title: "Same-Day Response", text: "Call before noon and in most cases your water heater is repaired or replaced that same day. Trucks stocked, techs dispatched, no 'next available appointment is Thursday.'", icon: Timer },
  { n: "02", title: "Upfront, Flat-Rate Pricing", text: "The exact price in writing before any work begins — pulled from the same flat-rate book at 2pm or 2am. The final bill matches the quote, to the dollar.", icon: DollarSign },
  { n: "03", title: "Factory-Trained Specialists", text: "Our techs work on water heaters every single day — Bradford White, Rheem, Navien, Rinnai and more. Not general plumbers who see one a month.", icon: Award },
  { n: "04", title: "Right the First Time", text: "Permitted, inspected, code-compliant installs with a 2-year labor warranty on top of the manufacturer's. Satisfaction guaranteed, in writing.", icon: BadgeCheck },
];

export default function Home() {
  useEffect(() => {
    document.title = "Water Heater Repair & Installation in Irvine, CA | Irvine Water Heater Man";
  }, []);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <div>
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative overflow-hidden navy-grain" data-testid="hero-section">
        <motion.div
          className="absolute inset-0 opacity-25"
          style={{ y: bgY, backgroundImage: `url(${IMAGES.hero})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B192C]/70 via-[#0B192C]/40 to-[#0B192C]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-16 sm:pb-28 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
          <div className="text-white">
            <MaskedLine delay={0.05}>
              <span className="inline-flex items-center gap-2 bg-[#FF6500]/15 border border-[#FF6500]/30 text-[#FF9A5C] font-heading font-bold text-xs sm:text-sm uppercase tracking-widest rounded-full px-4 py-1.5">
                <AlarmClock className="w-4 h-4" /> 24/7 Emergency Service · Irvine &amp; Orange County
              </span>
            </MaskedLine>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6 mb-6">
              <MaskedLine delay={0.15}>Water Heater Repair</MaskedLine>
              <MaskedLine delay={0.3}>
                <span>&amp; Installation in</span>
              </MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="text-[#FF6500]">Irvine, CA.</span>
              </MaskedLine>
            </h1>
            <MaskedLine delay={0.6}>
              <p className="text-white/70 text-lg sm:text-xl leading-relaxed max-w-xl">
                Tank or tankless, gas or electric — repaired or replaced the same day, at a price you approve before we start.
              </p>
            </MaskedLine>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap items-center gap-4 mt-8"
            >
              <a href={PHONE_HREF} data-testid="hero-call-btn" className="inline-flex items-center gap-3 bg-[#FF6500] text-white font-heading font-bold text-lg sm:text-xl px-7 py-4 rounded-full hover:bg-[#E85C0D] hover:scale-105 transition-[background-color,transform]">
                <Phone className="w-5 h-5" /> Call {PHONE}
              </a>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
                </span>
                Live answer, right now
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl"
            data-testid="hero-quote-card"
          >
            <h2 className="font-heading font-extrabold text-2xl text-gray-900 mb-1">Get a Free Estimate</h2>
            <p className="text-gray-500 text-sm mb-5">Takes 20 seconds. A specialist calls you right back.</p>
            <QuoteForm idPrefix="hero" />
          </motion.div>
        </div>
      </section>

      {/* ============ TRUST BAR ============ */}
      <section className="bg-white border-b border-gray-200" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Licensed & Insured", s: "CA C-36 Plumbing Contractor" },
            { icon: Award, t: "15+ Years in OC", s: "Thousands of heaters serviced" },
            { icon: Clock, t: "24/7 Emergency", s: "Live answer, day or night" },
            { icon: MapPin, t: "Irvine Based", s: "All of Orange County" },
          ].map((b) => (
            <div key={b.t} className="flex items-center gap-3">
              <b.icon className="w-8 h-8 text-[#FF6500] shrink-0" />
              <div>
                <p className="font-heading font-bold text-gray-900 text-sm sm:text-base leading-tight">{b.t}</p>
                <p className="text-gray-500 text-xs sm:text-sm">{b.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ WH SERVICES GRID ============ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#FAFAFA]" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-[#FF6500] font-heading font-bold uppercase tracking-widest text-sm mb-3">What We Do Best</p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-gray-900 max-w-2xl mb-4">
              Water Heaters. It's In The Name.
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mb-12">
              Not a general plumber who "also does" water heaters — specialists who install, repair, and replace them every single day.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WH_SERVICES.map((s, i) => {
              const Icon = serviceIcons[s.slug];
              return (
                <Reveal key={s.slug} delay={i * 0.07}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group block h-full bg-white border border-gray-200 rounded-2xl p-7 hover:border-[#FF6500] hover:-translate-y-1 transition-[border-color,transform] duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#0B192C] flex items-center justify-center mb-5 group-hover:bg-[#FF6500] transition-colors">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">{s.name}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{s.short}</p>
                    <span className="inline-flex items-center gap-1.5 text-[#FF6500] font-heading font-bold text-sm">
                      Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <BrandsMarquee />

      {/* ============ MANIFESTO / WHY US ============ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 navy-grain text-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-[#FF6500] font-heading font-bold uppercase tracking-widest text-sm mb-3">Why Irvine Calls Us First</p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight max-w-2xl mb-16">
              The Water Heater Standard.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
            {manifesto.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div className="flex gap-6">
                  <span className="font-heading font-black text-5xl sm:text-6xl text-stroke leading-none select-none">{m.n}</span>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <m.icon className="w-5 h-5 text-[#FF6500]" />
                      <h3 className="font-heading font-bold text-xl sm:text-2xl">{m.title}</h3>
                    </div>
                    <p className="text-white/60 leading-relaxed">{m.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-[#FF6500] font-heading font-bold uppercase tracking-widest text-sm mb-3">Transparent Pricing</p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-gray-900 mb-6">
              Know The Number Before We Start.
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              A water heater is a big-ticket purchase. You deserve real ranges up front — and a written flat-rate quote before a single tool comes off the truck. Financing available on replacements and conversions.
            </p>
            <a href={PHONE_HREF} data-testid="pricing-call-btn" className="inline-flex items-center gap-2 bg-[#0B192C] text-white font-heading font-bold px-6 py-3.5 rounded-full hover:bg-[#FF6500] transition-colors">
              <Phone className="w-4 h-4" /> Get an Exact Quote
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid gap-4">
              {[
                { t: "Water Heater Repair", p: "$189 – $450", s: "Diagnostic waived with repair" },
                { t: "Tank Replacement (40–50 gal)", p: "$1,450 – $2,100", s: "Haul-away & code upgrades included" },
                { t: "Tankless Conversion", p: "$3,800 – $5,500", s: "Gas line, venting & permit included" },
                { t: "Tankless Descale / Maintenance", p: "$189", s: "Recommended annually" },
              ].map((row) => (
                <div key={row.t} className="flex items-center justify-between gap-4 border border-gray-200 rounded-xl p-5 hover:border-[#FF6500] transition-colors">
                  <div>
                    <p className="font-heading font-bold text-gray-900">{row.t}</p>
                    <p className="text-gray-500 text-sm">{row.s}</p>
                  </div>
                  <p className="font-heading font-extrabold text-lg sm:text-xl text-[#0B192C] whitespace-nowrap">{row.p}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#FAFAFA]" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                <p className="text-[#FF6500] font-heading font-bold uppercase tracking-widest text-sm mb-3">Homeowner Reviews</p>
                <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-gray-900">
                  Irvine Trusts Us With Its Hot Water.
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-heading font-black text-5xl text-gray-900">5.0</span>
                <div>
                  <Stars />
                  <p className="text-gray-500 text-sm mt-1">Google-style rating</p>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REVIEWS.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.06}>
                <div className="bg-white border border-gray-200 rounded-2xl p-7 h-full flex flex-col" data-testid={`review-card-${i}`}>
                  <Stars />
                  <p className="text-gray-700 leading-relaxed mt-4 mb-6 flex-1">"{r.text}"</p>
                  <div>
                    <p className="font-heading font-bold text-gray-900">{r.name}</p>
                    <p className="text-gray-500 text-sm">{r.area} · {r.service}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="text-center mt-10">
              <Link to="/reviews" data-testid="all-reviews-link" className="inline-flex items-center gap-2 font-heading font-bold text-[#FF6500] hover:gap-3 transition-[gap]">
                Read all reviews <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ SERVICE AREA ============ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-white" data-testid="service-area-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden">
              <img src={IMAGES.van} alt="Service van in an Irvine neighborhood" className="w-full h-[420px] object-cover" />
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur rounded-xl px-5 py-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#FF6500]" />
                <span className="font-heading font-bold text-gray-900">Based in Irvine · Serving All of OC</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#FF6500] font-heading font-bold uppercase tracking-widest text-sm mb-3">Service Area</p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-gray-900 mb-6">
              Your Neighborhood Is Our Route.
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              Every Irvine village and the surrounding Orange County cities — usually within the hour for emergencies.
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {NEIGHBORHOODS.map((n) => (
                <span key={n} className="bg-[#FAFAFA] border border-gray-200 rounded-full px-4 py-1.5 text-sm font-semibold text-gray-700">{n}</span>
              ))}
              {NEARBY_CITIES.map((n) => (
                <span key={n} className="bg-[#0B192C] text-white rounded-full px-4 py-1.5 text-sm font-semibold">{n}</span>
              ))}
            </div>
            <Link to="/service-area" data-testid="service-area-link" className="inline-flex items-center gap-2 font-heading font-bold text-[#FF6500] hover:gap-3 transition-[gap]">
              See full service area <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ SECONDARY PLUMBING ============ */}
      <section className="py-16 px-4 sm:px-6 bg-[#FAFAFA] border-y border-gray-200" data-testid="plumbing-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-gray-900 mb-2">
              Also Handled: General Plumbing.
            </h2>
            <p className="text-gray-500 mb-8">Water heaters are the specialty — but our licensed plumbers cover the rest of the house too.</p>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-4">
            {PLUMBING_SERVICES.map((s, i) => {
              const Icon = serviceIcons[s.slug];
              return (
                <Reveal key={s.slug} delay={i * 0.06}>
                  <Link to={`/services/${s.slug}`} data-testid={`plumbing-card-${s.slug}`} className="group flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-[#FF6500] transition-colors">
                    <Icon className="w-6 h-6 text-[#0B192C] group-hover:text-[#FF6500] transition-colors shrink-0" />
                    <div className="flex-1">
                      <p className="font-heading font-bold text-gray-900">{s.name}</p>
                      <p className="text-gray-500 text-xs">{s.priceNote} from {s.priceFrom}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF6500] group-hover:translate-x-1 transition-[color,transform]" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand idPrefix="home-cta" />
    </div>
  );
}
